<template>
  <div
    class="group relative flex flex-col border rounded-xl p-4 flex-1 min-w-0 transition-colors duration-150 cursor-default"
    :class="[
      'border-zinc-100 dark:border-zinc-800 hover:border-primary dark:hover:border-primary'
    ]"
  >
    <div class="flex items-start justify-between gap-2 mb-2.5 min-h-[20px]">
      <span
        v-if="entry.variant_label"
        class="inline-flex items-center gap-1 border border-zinc-200 dark:border-zinc-700 rounded-full text-[10px] font-medium text-zinc-500 dark:text-zinc-400 px-2 py-0.5 leading-tight truncate max-w-[180px] flex-shrink-0"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-gold-400 flex-shrink-0"></span>
        {{ entry.variant_label }}
      </span>
      <span v-else class="flex-1" />

      <div class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0 ml-auto">
        <button
          @click.stop="$emit('edit')"
          class="p-1 rounded text-zinc-300 hover:text-primary dark:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
          title="Edit"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
          </svg>
        </button>
        <button
          @click.stop="$emit('archive')"
          class="p-1 rounded text-zinc-300 hover:text-red-400 dark:text-zinc-600 dark:hover:text-red-400 transition-colors"
          title="Archive"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
          </svg>
        </button>
      </div>
    </div>

    <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug">{{ title }}</p>

    <p v-if="subtitle" class="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5 leading-snug" :class="entryType === 'skill' ? 'line-clamp-2' : ''">{{ subtitle }}</p>

    <p v-if="dateRange" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ dateRange }}</p>

    <template v-if="bullets.length">
      <div class="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800 flex flex-col gap-1">
        <p
          v-for="b in bullets.slice(0, 3)"
          :key="b"
          class="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed line-clamp-1"
        ><span class="text-gold font-bold mr-1">·</span>{{ b }}</p>
        <p v-if="bullets.length > 3" class="text-[10px] text-zinc-400 dark:text-zinc-500 mt-0.5">
          +{{ bullets.length - 3 }} more
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default {
  name: 'ProfileEntryCard',
  props: {
    entry:     { type: Object, required: true },
    entryType: { type: String, required: true }
  },
  emits: ['edit', 'archive'],
  setup(props) {
    const title = computed(() => {
      switch (props.entryType) {
        case 'experience': return props.entry.job_title
        case 'education':  return props.entry.institution
        case 'activity':   return props.entry.organization
        case 'skill':      return props.entry.category_name
        default:           return ''
      }
    })

    const subtitle = computed(() => {
      switch (props.entryType) {
        case 'experience':
          return [props.entry.company, props.entry.employment_type].filter(Boolean).join(' · ')
        case 'education':
          return [props.entry.program_name, props.entry.field_of_study].filter(Boolean).join(', ')
        case 'activity':
          return props.entry.role || ''
        case 'skill':
          return props.entry.content || ''
        default:
          return ''
      }
    })

    const dateRange = computed(() => {
      if (props.entryType === 'skill') return null
      const { start_month, start_year, end_month, end_year, is_current } = props.entry
      const fmt = (m, y) => [m ? MONTHS[m - 1] : null, y].filter(Boolean).join(' ')
      const start = start_year ? fmt(start_month, start_year) : null
      const end   = is_current ? 'Present' : end_year ? fmt(end_month, end_year) : null
      if (!start && !end) return null
      return [start, end].filter(Boolean).join(' – ')
    })

    const bullets = computed(() => {
      if (props.entryType === 'skill') return []
      return (props.entry.base_bullet_points || []).filter(b => b?.trim())
    })

    return { title, subtitle, dateRange, bullets }
  }
}
</script>
